import React, { ReactNode } from 'react'

import { secondsToMilliseconds } from 'date-fns'
import { FormattedDate, FormattedMessage, FormattedNumber, IntlShape } from 'react-intl'

import { EligibilityPlanToDisplay } from '@/types'
import { isP1X, priceFromCents } from '@/utils'
import s from '@/utils/paymentPlanStrings.module.css'
import { isCredit, isPayLater, requiresLegalDisclosure } from '@/utils/regulatoryFigures'

export const paymentPlanShorthandName = (payment: EligibilityPlanToDisplay): ReactNode => {
  const {
    deferred_days: deferredDays,
    deferred_months: deferredMonths,
    installments_count: installmentsCount,
  } = payment

  if (isP1X(payment)) {
    return (
      <FormattedMessage
        id="payment-plan-strings.pay.now.button"
        defaultMessage="Payer maintenant"
      />
    )
  }
  if (installmentsCount === 1 && deferredDays) {
    return (
      <FormattedMessage
        id="payment-plan-strings.day-abbreviation"
        defaultMessage="J{deferredDays}"
        values={{
          deferredDays: `+${deferredDays}`,
        }}
      />
    )
  }
  if (installmentsCount === 1 && deferredMonths) {
    return (
      <FormattedMessage
        id="payment-plan-strings.month-abbreviation"
        defaultMessage="M{deferredMonths}"
        values={{
          deferredMonths: `+${deferredMonths}`,
        }}
      />
    )
  }
  return `${installmentsCount}x`
}

/**
 * String version of paymentPlanShorthandName that returns a translated string
 * instead of a ReactNode for use in aria-label attributes for example
 */
export const paymentPlanShorthandText = (
  payment: EligibilityPlanToDisplay,
  intl: IntlShape,
): string => {
  const {
    deferred_days: deferredDays,
    deferred_months: deferredMonths,
    installments_count: installmentsCount,
  } = payment

  if (isP1X(payment)) {
    return intl.formatMessage({
      id: 'payment-plan-strings.pay.now.button',
      defaultMessage: 'Payer maintenant',
    })
  }
  if (installmentsCount === 1 && deferredDays) {
    return intl.formatMessage(
      {
        id: 'payment-plan-strings.day-abbreviation',
        defaultMessage: 'J{deferredDays}',
      },
      {
        deferredDays: `+${deferredDays}`,
      },
    )
  }
  if (installmentsCount === 1 && deferredMonths) {
    return intl.formatMessage(
      {
        id: 'payment-plan-strings.month-abbreviation',
        defaultMessage: 'M{deferredMonths}',
      },
      {
        deferredMonths: `+${deferredMonths}`,
      },
    )
  }
  return `${installmentsCount}x`
}

// eslint-disable-next-line consistent-return
const withNoFee = (payment: EligibilityPlanToDisplay) => {
  if (
    payment.payment_plan?.every((plan) => plan.customer_fee === 0 && plan.customer_interest === 0)
  ) {
    return (
      <>
        {' '}
        <FormattedMessage id="payment-plan-strings.no-fee" defaultMessage="(sans frais)" />
      </>
    )
  }
}

const knowMoreLine = (payment: EligibilityPlanToDisplay): ReactNode => {
  if (!payment.eligible || !requiresLegalDisclosure(payment)) return null
  return (
    <p className={s.openModalInfo}>
      <FormattedMessage
        id="payment-plan-strings.credit"
        defaultMessage="Cliquez pour en savoir plus"
        description="Link to credit details"
      />
    </p>
  )
}

export const paymentPlanInfoText = (payment: EligibilityPlanToDisplay): ReactNode => {
  const {
    deferred_days: deferredDays,
    deferred_months: deferredMonths,
    installments_count: installmentsCount,
    eligible,
    purchase_amount: purchaseAmount,
    minAmount = 0,
    maxAmount = 0,
    payment_plan: paymentPlan,
  } = payment
  const deferredDaysCount = deferredDays + deferredMonths * 30

  if (!eligible) {
    return (
      <p>
        {purchaseAmount > maxAmount ? (
          <FormattedMessage
            id="payment-plan-strings.ineligible-greater-than-max"
            defaultMessage="Jusqu'à {maxAmount}"
            values={{
              maxAmount: (
                <FormattedNumber
                  value={priceFromCents(maxAmount)}
                  style="currency"
                  currency="EUR"
                />
              ),
            }}
          />
        ) : (
          <FormattedMessage
            id="payment-plan-strings.ineligible-lower-than-min"
            defaultMessage="À partir de {minAmount}"
            values={{
              minAmount: (
                <FormattedNumber
                  value={priceFromCents(minAmount)}
                  style="currency"
                  currency="EUR"
                />
              ),
            }}
          />
        )}
      </p>
    )
  }
  if (!paymentPlan) {
    /* This error should never happen. We added this condition to avoid a typescript warning on
         payment_plan possibly undefined. As far as we know, it only happens when the plan is not
         eligible, which is checked above. */
    throw Error(
      `No payment plan provided for payment in ${installmentsCount} installments. Please contact us if you see this error.`,
    )
  }

  if (deferredDaysCount !== 0 && installmentsCount === 1) {
    return (
      <>
        <p>
          <FormattedMessage
            id="payment-plan-strings.deferred"
            defaultMessage="{totalAmount} à payer le {dueDate}"
            values={{
              totalAmount: (
                <FormattedNumber
                  value={priceFromCents(paymentPlan[0].total_amount)}
                  style="currency"
                  currency="EUR"
                />
              ),
              dueDate: (
                <FormattedDate
                  value={secondsToMilliseconds(paymentPlan[0].due_date)}
                  day="numeric"
                  month="long"
                  year="numeric"
                />
              ),
            }}
          />
          {withNoFee(payment)}
        </p>
        {knowMoreLine(payment)}
      </>
    )
  }

  if (isP1X(payment)) {
    return (
      <p>
        <FormattedMessage
          id="payment-plan-strings.pay-now"
          defaultMessage="Payer maintenant {totalAmount}"
          values={{
            totalAmount: (
              <FormattedNumber
                value={priceFromCents(paymentPlan[0].total_amount)}
                style="currency"
                currency="EUR"
              />
            ),
            installmentsCount,
          }}
        />
        {withNoFee(payment)}
      </p>
    )
  }

  if (isCredit(payment)) {
    return knowMoreLine(payment)
  }

  const areInstallmentsOfSameAmount = paymentPlan.every(
    (installment, index) => index === 0 || installment.total_amount === paymentPlan[0].total_amount,
  )

  if (areInstallmentsOfSameAmount) {
    return (
      <>
        <p>
          <FormattedMessage
            id="payment-plan-strings.multiple-installments-same-amount"
            defaultMessage="{installmentsCount} x {totalAmount}"
            values={{
              totalAmount: (
                <FormattedNumber
                  value={priceFromCents(paymentPlan[0].total_amount)}
                  style="currency"
                  currency="EUR"
                />
              ),
              installmentsCount,
            }}
          />
          {withNoFee(payment)}
        </p>
        {knowMoreLine(payment)}
      </>
    )
  }

  return (
    <>
      <p>
        <FormattedMessage
          id="payment-plan-strings.multiple-installments"
          defaultMessage="{numberOfRemainingInstallments, plural, one {{firstInstallmentAmount} puis {numberOfRemainingInstallments} x {othersInstallmentAmount}} other {{firstInstallmentAmount} puis {numberOfRemainingInstallments} x {othersInstallmentAmount}}}"
          values={{
            firstInstallmentAmount: (
              <FormattedNumber
                value={priceFromCents(paymentPlan[0].total_amount)}
                style="currency"
                currency="EUR"
              />
            ),
            numberOfRemainingInstallments: installmentsCount - 1,
            othersInstallmentAmount: (
              <FormattedNumber
                value={priceFromCents(paymentPlan[1].total_amount)}
                style="currency"
                currency="EUR"
              />
            ),
          }}
        />
        {withNoFee(payment)}
      </p>
      {knowMoreLine(payment)}
    </>
  )
}

export const getPlanDescription = (plan: EligibilityPlanToDisplay, intl: IntlShape) => {
  if (isP1X(plan)) {
    return intl.formatMessage({
      id: 'payment-plan-strings.pay.now.button',
      defaultMessage: 'Payer maintenant',
    })
  }
  if (isPayLater(plan)) {
    return intl.formatMessage(
      {
        id: 'payment-plan-strings.pay.deferred.button',
        defaultMessage: 'Payer en différé : {deferredTime}',
      },
      {
        deferredTime:
          plan.deferred_months > 0
            ? intl.formatMessage(
                {
                  id: 'payment-plan-strings.deferred.months',
                  defaultMessage: '{months, number} {months, plural, one {mois} other {mois}}',
                },
                { months: plan.deferred_months },
              )
            : intl.formatMessage(
                {
                  id: 'payment-plan-strings.deferred.days',
                  defaultMessage: '{days, number} {days, plural, one {jour} other {jours}}',
                },
                { days: plan.deferred_days },
              ),
      },
    )
  }

  return `${plan.installments_count}x`
}
